import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';

import { ModalNextProvider, Icon, Loader } from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { ServiceManager } from '@jutro/services';

import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { messages as commonMessages } from 'gw-platform-translations';
import lobIconUtil from 'gw-portals-util-js/lobIconUtil';

import PolicyCommonDocuments
    from 'gw-capability-policy-common-react/components/PolicyCommonDocuments/PolicyCommonDocuments';
import { DocumentService } from 'gw-capability-policydocument';

import metadata from './DocumentSummaryPage.metadata.json5';
import styles from './DocumentSummaryPage.module.scss';
import messages from './DocumentSummaryPage.messages';

const getTabHeader = (productCode, label = '') => {
    const icon = lobIconUtil.getMaterialIcon(productCode);
    return (
        <div id={`divFor${productCode}${label}`} className={styles.tabContent}>
            {productCode ? (
                <Icon id={`iconFor${productCode}${label}`} icon={icon} title={label} />
            ) : null}
            <span id={`labelFor${productCode}${label}`}>{label.toUpperCase()}</span>
        </div>
    );
};

function DocumentSummaryPage(props) {
    const { match, authHeader, history } = props;
    const { params } = match;
    const { streamId } = params;

    const localeService = ServiceManager.getService('locale-service');
    const defaultCurrencyCode = localeService.getDefaultCurrencyCode().toUpperCase();

    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );

    const { PolicyService } = useDependencies('PolicyService');

    // screen states
    const [isLoadingData, updateIsLoadingData] = useState(true);
    const [selectedInvoiceStreamId, updateSelectedInvoiceStreamId] = useState(streamId);
    const [selectedPolicyNumber, updateSelectedPolicyNumber] = useState(streamId);
    const [selectedCurrency, updateSelectedCurrency] = useState(defaultCurrencyCode);
    const [activeTab, updateActiveTab] = useState(selectedInvoiceStreamId);
    const [writableDocuments, updateWritableDocuments] = useState([]);

    // fetched data
    const [accountDocumentSummaryFromApi, setAccountDocumentSummaryFromApi] = useState({});
    const [invoiceStreamDetailsMapFromApi, setInvoiceStreamDetailsMapFromApi] = useState(new Map());
    const [policySummariesFromApi, setPolicySummariesFromApi] = useState([]);
    const [accountDocumentDataFromApi, setAccountDocumentDataFromApi] = useState([]);

    const { accountLevelBilling = false, invoiceStreams = [] } = accountDocumentSummaryFromApi;

    const getCellClaimNumber = (rowData) => {
        // fetchInvoiceDetailsByStreamId(rowData);
        return (
            <span>
                testdata
            </span>
        );
    };

    const updateMap = useCallback(
        (invoiceStreamId) => (fetchData) => {
            setInvoiceStreamDetailsMapFromApi(
                invoiceStreamDetailsMapFromApi.set(invoiceStreamId, fetchData)
            );

            const { documentDTOs } = fetchData;
            const { currency } = documentDTOs;
            updateSelectedCurrency(currency.toUpperCase());
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const fetchAccountLevelInvoiceStreams = useCallback(() => {
        let accountInvoicePromise;
        let policySummaryPromise;

        if (_.isEmpty(accountDocumentDataFromApi)) { 
            //accountInvoicePromise = PolicyService.getAccountDocuments(
            accountInvoicePromise = PolicyService.getUserDocuments_EY(
                authHeader
            ).then((accountPolicies) => {
                const {
                    accountLevelBilling: accountLevelBillingFromApi = false,
                    invoiceStreams: invoiceStreamsFromApi = accountPolicies
                } = accountPolicies;

                let defaultPolicyNumber;
                if (invoiceStreamsFromApi.length > 0) {
                    defaultPolicyNumber = invoiceStreamsFromApi[0].policyNumber;
                }
                if (accountLevelBillingFromApi && invoiceStreamsFromApi.length === 1) {
                    defaultPolicyNumber = invoiceStreamsFromApi[0].policyNumber;
                    updateSelectedInvoiceStreamId(invoiceStreamsFromApi[0].policyNumber);
                    updateActiveTab(invoiceStreamsFromApi[0].policyNumber);
                }

                setAccountDocumentDataFromApi(accountPolicies);
                return defaultPolicyNumber;
            });
        } else {
            accountInvoicePromise = Promise.resolve(accountDocumentDataFromApi);
        }

        // if (accountDocumentDataFromApi.length === 0) {
        //     policySummaryPromise = PolicyService.getAccountPolicySummaries(authHeader).then(
        //         setPolicySummariesFromApi
        //     );
        // } else {
        //     policySummaryPromise = Promise.resolve(policySummariesFromApi);
        // }

        return Promise.all([accountInvoicePromise, policySummaryPromise]).catch(() => {
            ModalNextProvider.showAlert({
                title: messages.failedToLoadPolices,
                message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                history.push('/home');
            }, _.noop);
        });
    }, [
        BillingService,
        PolicyService,
        history,
        authHeader,
        accountDocumentSummaryFromApi,
        policySummariesFromApi,
        accountDocumentDataFromApi
    ]);

    const getPaymentDetailsGroupFromMap = useCallback(
        (invoiceStreamId) => {
            return invoiceStreamDetailsMapFromApi.get(invoiceStreamId);
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const fetchInvoiceDetailsByStreamId = useCallback(
        (label, forceRefresh = false) => {
            const invoiceDetails = getPaymentDetailsGroupFromMap(label); // alert(invoiceDetails);
            let invoiceDetailsPromise;

            if ((label && _.isEmpty(invoiceDetails)) || forceRefresh) {
                invoiceDetailsPromise = PolicyService.getAccountPolicyDetails(
                    label,
                    authHeader
                )
                    // .then(updateMap(label))
                    .catch(() => {
                        ModalNextProvider.showAlert({
                            title: messages.failedToLoadPolices,
                            message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).then(() => {
                            history.push('/home');
                        }, _.noop);
                    });
            } else {
                invoiceDetailsPromise = Promise.resolve(invoiceDetails);
            }


            return invoiceDetailsPromise;
        },
        [getPaymentDetailsGroupFromMap, BillingService, authHeader, updateMap, history]
    );

    const selectedLobCode = useMemo(() => {
        const foundInvoiceStreams = invoiceStreams.filter(
            (item) => item.policyNumber === selectedInvoiceStreamId
        );
        return foundInvoiceStreams.length > 0 ? foundInvoiceStreams[0].lobcode : undefined;
    }, [accountDocumentDataFromApi, selectedInvoiceStreamId]);

    const onTabClick = useCallback(
        (invoiceStreamIdFromTabId) => {
            // const filterDocument = accountDocumentDataFromApi.filter((x) => { return x.policyNumber === invoiceStreamIdFromTabId; });
            fetchInvoiceDetailsByStreamId(invoiceStreamIdFromTabId).finally(() => {
                updateActiveTab(invoiceStreamIdFromTabId);
                updateSelectedInvoiceStreamId(invoiceStreamIdFromTabId);
            });
            const resp = fetchInvoiceDetailsByStreamId(invoiceStreamIdFromTabId);
        },
        [accountDocumentDataFromApi]
    );

    const reloadInvoiceDetails = useCallback(
        (invoiceStreamId) => {
            updateIsLoadingData(true);
            return fetchInvoiceDetailsByStreamId(invoiceStreamId, true).finally(() => {
                updateIsLoadingData(false);
            });
        },
        [fetchInvoiceDetailsByStreamId]
    );

    const getPolicyDownloadLink = useCallback((item) => {
        return DocumentService.downloadPolicyDocument(
            _.get(item, 'idCardPublicID'),
            _.get(item, 'idCardSessionID')
        );
    }, [accountDocumentDataFromApi]);

    useEffect(() => {
        updateIsLoadingData(true);
        fetchAccountLevelInvoiceStreams().then((retValues) => {
            if (retValues) {
                const initStreamId = selectedPolicyNumber || retValues[0];
                fetchInvoiceDetailsByStreamId(initStreamId).finally(() => {
                    onTabClick(initStreamId);
                    updateIsLoadingData(false);
                });
            }
        });
        // execure once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const noData = useCallback(
        () => {
            return accountDocumentDataFromApi.length === 0;
        },
        [accountDocumentDataFromApi]
    );

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {
        noDataMessageContainer: {
            visible: noData(),
            message: messages.noBillingData
        },
        documentSummaryContainer: {
            visible: !noData(),
        },
        documentTabsId: {
            content: _.map(accountDocumentDataFromApi, (item) => {
                return {
                    id: item.policyNumber,
                    type: 'element',
                    component: 'tab',
                    componentProps: {
                        heading: getTabHeader('', item.policyNumber)
                    },
                    content: [
                        {
                            id: `documentContentFor${item.policyNumber}`,
                            type: 'container',
                            component: PolicyCommonDocuments,
                            componentProps: {
                                value: item,
                                updateWritableDocuments: updateWritableDocuments,
                                writableDocuments: writableDocuments,
                                canUploadDocument: true
                            }
                        }
                    ]
                };
            }),
            activeTab: activeTab,
            onTabChange: onTabClick,
            policyDocumentsSection: {
                updateWritableDocuments: updateWritableDocuments,
                writableDocuments: writableDocuments
            }
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            policycommondocumentscomponent: PolicyCommonDocuments,
        },
    };


    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

DocumentSummaryPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            streamId: PropTypes.string
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({}).isRequired
};

export default withAuthenticationContext(withRouter(DocumentSummaryPage));
